import {
  Table as AriaTable,
  TableHeader as AriaTableHeader,
  TableBody as AriaTableBody,
  Column as AriaColumn,
  Row as AriaRow,
  Cell as AriaCell,
  CheckboxField as AriaCheckboxField,
  CheckboxButton as AriaCheckboxButton,
  Button as AriaButton,
  composeRenderProps,
  type TableProps as AriaTableProps,
  type TableHeaderProps as AriaTableHeaderProps,
  type TableBodyProps as AriaTableBodyProps,
  type ColumnProps as AriaColumnProps,
  type RowProps as AriaRowProps,
  type CellProps as AriaCellProps,
} from 'react-aria-components';
import {
  CaretDownIcon,
  CaretRightIcon,
  CaretUpIcon,
} from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { CheckboxIndicator } from './Checkbox';
import { cn } from './lib/cn';

/*
 * Rows are separated by cell borders, not a row border: `border-separate` is
 * the only mode where a sticky `<th>` keeps its edge while rows scroll under it.
 */

export type TableContainerProps = {
  children: ReactNode;
  className?: string;
};

/** Scroll port for the table. The sticky header needs a scrolling ancestor. */
export function TableContainer({ children, className }: TableContainerProps) {
  return (
    <div className={cn('sk:relative sk:w-full sk:overflow-auto', className)}>
      {children}
    </div>
  );
}

/* Re-exported so apps can type sort and selection handlers without depending on
   React Aria. */
export type { Selection, SortDescriptor } from 'react-aria-components';

export type TableProps = AriaTableProps & { className?: string };

export function Table({ className, ...props }: TableProps) {
  return (
    <AriaTable
      {...props}
      className={cn(
        'sk:w-full sk:border-separate sk:border-spacing-0 sk:text-left',
        'sk:text-dense sk:text-fg',
        className,
      )}
    />
  );
}

export type TableHeaderProps<T extends object> = AriaTableHeaderProps<T> & {
  className?: string;
};

export function TableHeader<T extends object>({
  className,
  ...props
}: TableHeaderProps<T>) {
  return (
    <AriaTableHeader
      {...props}
      className={cn('sk:sticky sk:top-0 sk:z-10 sk:bg-surface', className)}
    />
  );
}

export type ColumnProps = AriaColumnProps & { className?: string };

export function Column({ className, children, ...props }: ColumnProps) {
  return (
    <AriaColumn
      {...props}
      className={composeRenderProps(className, resolved =>
        cn(
          'sk:group sk:border-b sk:border-rule sk:bg-surface',
          /* The header paints its own background over the card's top corners. */
          'sk:first:rounded-tl-card sk:last:rounded-tr-card',
          'sk:px-3 sk:py-2 sk:text-dense sk:font-medium sk:text-fg-secondary',
          'sk:whitespace-nowrap sk:outline-none',
          'sk:rac-focus-visible:outline-2 sk:rac-focus-visible:outline-solid sk:rac-focus-visible:-outline-offset-2 sk:rac-focus-visible:outline-primary',
          props.allowsSorting ? 'sk:cursor-pointer sk:hover:text-fg' : '',
          resolved,
        ),
      )}
    >
      {composeRenderProps(
        children,
        (resolved, { allowsSorting, sortDirection }) => (
          <span className="sk:flex sk:items-center sk:gap-1">
            {resolved}
            {allowsSorting ? (
              <span
                aria-hidden="true"
                className={
                  sortDirection
                    ? 'sk:text-fg'
                    : 'sk:text-fg-tertiary sk:opacity-0 sk:transition-opacity sk:group-hover:opacity-100'
                }
              >
                {sortDirection === 'descending' ? (
                  <CaretDownIcon size={11} weight="bold" />
                ) : (
                  <CaretUpIcon size={11} weight="bold" />
                )}
              </span>
            ) : null}
          </span>
        ),
      )}
    </AriaColumn>
  );
}

export type TableBodyProps<T extends object> = AriaTableBodyProps<T> & {
  className?: string;
};

export function TableBody<T extends object>(props: TableBodyProps<T>) {
  return <AriaTableBody {...props} />;
}

export type RowProps<T extends object> = AriaRowProps<T> & {
  className?: string;
  /**
   * A child row shown under an expanded parent. Expanding is the parent's
   * own state: render the child rows right after it while it is open.
   */
  isNested?: boolean;
};

/* The hover tint lives on the cells: a `<tr>` ignores its own radius. */
export function Row<T extends object>({
  className,
  isNested,
  ...props
}: RowProps<T>) {
  return (
    <AriaRow
      {...props}
      className={composeRenderProps(className, resolved =>
        cn(
          'sk:group sk:outline-none',
          /* Child content starts where the parent's text does, past its caret,
             and a guide line down the indent ties it to the parent. */
          isNested
            ? 'sk:[&>td:first-child]:pl-10 sk:[&>td:first-child]:before:absolute sk:[&>td:first-child]:before:inset-y-0 sk:[&>td:first-child]:before:left-5 sk:[&>td:first-child]:before:w-px sk:[&>td:first-child]:before:bg-rule sk:[&>td:first-child]:before:content-[""]'
            : '',
          props.onAction || props.href ? 'sk:cursor-pointer' : '',
          'sk:rac-focus-visible:outline-2 sk:rac-focus-visible:outline-solid sk:rac-focus-visible:-outline-offset-2 sk:rac-focus-visible:outline-primary',
          resolved,
        ),
      )}
    />
  );
}

export type CellProps = AriaCellProps & { className?: string };

export function Cell({ className, ...props }: CellProps) {
  return (
    <AriaCell
      {...props}
      className={composeRenderProps(className, resolved =>
        cn(
          'sk:border-b sk:border-rule sk:px-3 sk:py-2 sk:align-middle',
          /* The last row leaves its edge to whatever follows the table. */
          'sk:group-last:border-b-0',
          /* The outer cells draw their rule inside their padding, so it stops
             short of the card edge and still meets the plain ones between. */
          'sk:relative sk:first:border-b-0 sk:last:border-b-0',
          'sk:first:after:absolute sk:first:after:left-3 sk:first:after:right-0 sk:first:after:bottom-0 sk:first:after:h-px sk:first:after:bg-rule sk:first:after:content-[""]',
          'sk:last:after:absolute sk:last:after:left-0 sk:last:after:right-3 sk:last:after:bottom-0 sk:last:after:h-px sk:last:after:bg-rule sk:last:after:content-[""]',
          'sk:group-last:after:hidden',
          'sk:group-hover:bg-rule',
          'sk:group-data-[selected]:bg-primary-bg',
          'sk:outline-none sk:rac-focus-visible:outline-2 sk:rac-focus-visible:outline-solid sk:rac-focus-visible:-outline-offset-2 sk:rac-focus-visible:outline-primary',
          resolved,
        ),
      )}
    />
  );
}

export type RowExpandButtonProps = {
  isExpanded: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
  'aria-label'?: string;
  className?: string;
};

/** Caret that opens and closes a row's child rows. Place it in the row's first cell. */
export function RowExpandButton({
  isExpanded,
  onExpandedChange,
  'aria-label': ariaLabel = isExpanded ? 'Collapse row' : 'Expand row',
  className,
}: RowExpandButtonProps) {
  return (
    <AriaButton
      aria-label={ariaLabel}
      aria-expanded={isExpanded}
      onPress={() => onExpandedChange(!isExpanded)}
      className={cn(
        'sk:inline-flex sk:size-6 sk:shrink-0 sk:items-center sk:justify-center sk:rounded-sm',
        'sk:text-fg-secondary sk:hover:bg-fill-secondary sk:hover:text-fg sk:cursor-pointer',
        'sk:outline-none sk:rac-focus-visible:outline-2 sk:rac-focus-visible:outline-solid sk:rac-focus-visible:outline-primary',
        className,
      )}
    >
      <CaretRightIcon
        size={12}
        weight="bold"
        aria-hidden="true"
        className={cn(
          'sk:transition-transform sk:duration-150',
          isExpanded ? 'sk:rotate-90' : '',
        )}
      />
    </AriaButton>
  );
}

export type SelectionCheckboxProps = {
  className?: string;
  'aria-label'?: string;
};

/**
 * Checkbox for the selection column. `slot="selection"` wires it to the table's
 * selection state: select-all in the header, that row in a row.
 */
export function SelectionCheckbox({
  className,
  'aria-label': ariaLabel,
}: SelectionCheckboxProps) {
  return (
    <AriaCheckboxField
      slot="selection"
      aria-label={ariaLabel}
      className={cn('sk:inline-flex', className)}
    >
      <AriaCheckboxButton className="sk-checkbox sk:group/checkbox sk:inline-flex sk:items-center sk:outline-none">
        {({ isSelected, isIndeterminate }) => (
          <CheckboxIndicator
            isSelected={isSelected}
            isIndeterminate={isIndeterminate}
          />
        )}
      </AriaCheckboxButton>
    </AriaCheckboxField>
  );
}
